import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';

export function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show the install button
      setShowInstallButton(true);
      
      // Show banner after 3 seconds
      setTimeout(() => {
        setShowBanner(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowInstallButton(false);
    setShowBanner(false);
  };

  const handleCloseBanner = () => {
    setShowBanner(false);
  };

  if (!showInstallButton) {
    return null;
  }

  return (
    <>
      {/* Install Button in Navigation - Subtle styling */}
      <Button
        onClick={handleInstallClick}
        variant="ghost"
        className="text-gray-400 hover:text-white hover:bg-white/10 rounded-none uppercase tracking-wider text-xs font-medium hidden md:flex items-center gap-1.5 px-3 py-2"
      >
        <Download className="w-3.5 h-3.5" />
        Install
      </Button>

      {/* Mobile Install Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-accent text-accent-foreground p-4 shadow-lg z-50 md:hidden animate-slide-up">
          <div className="container mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-1">
                <img 
                  src="/icon-192x192.png" 
                  alt="Modular Furniture Store" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm">Install Modular Furniture</p>
                <p className="text-xs opacity-90">Quick access from your home screen</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleInstallClick}
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold text-xs px-4"
              >
                Install
              </Button>
              <button
                onClick={handleCloseBanner}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Install Button for Mobile (if banner is closed) */}
      {!showBanner && (
        <button
          onClick={handleInstallClick}
          className="fixed bottom-6 right-6 bg-primary text-white p-4 rounded-full shadow-lg hover:bg-primary/90 transition-all hover:scale-110 z-40 md:hidden"
          aria-label="Install App"
        >
          <Download className="w-6 h-6" />
        </button>
      )}
    </>
  );
}
